/**
 * apps/web :: pages :: ProfilePage
 *
 * The unified `/profile` route — combines what was split across
 * `/settings` and `/settings/access`. Lays out the screen the design at
 * `content/design/roadbridge-app/03-portal/Profile.png` specifies:
 *
 *   1. Profile header card (avatar, name, email, member-since,
 *      home-country chip, target-state chip, three stat pills).
 *   2. Membership Plan card(s) — active entitlements with cancel /
 *      reactivate, OR a single "trial / expired" card with the unlock
 *      CTA pointing to /pricing.
 *   3. Learning Preferences (target states, interface language, daily
 *      goal segmented control).
 *   4. Notifications (delegated to the existing
 *      `<NotificationPreferences />` component — DO NOT rewrite).
 *   5. Account (sign-in method readout, change-password link, delete-
 *      account via the existing `<DangerZone />` component).
 *
 * Routing:
 *   - Reachable when `useEntitlement().isExpired === true` (this is the
 *     ONE post-trial surface the user keeps full access to outside the
 *     dashboard's paywall body).
 *   - `/settings` and `/settings/access` redirect here in App.tsx.
 *
 * Data fetched on mount:
 *   - `useAuth()` — name, email, profile_picture_url, created_at_ms.
 *   - `getProfile()` — home country, native language, target states,
 *     current focus state.
 *   - `getProgress(currentFocusState)` — practice question count for
 *     the "Practice" stat pill (best-effort; falls back to 0 on error).
 *   - `useEntitlement()` — entitlements list for the Membership card.
 *
 * Audit-log discipline: every PUT /api/profile call here goes through
 * the typed `putProfile` helper which is the single audit-stamped write
 * path the backend instruments. No raw `apiFetch("/api/profile",
 * {method:"PUT"})` calls — we keep one chokepoint so the
 * profile-mutation audit row is guaranteed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DangerZone from "@/components/DangerZone";
import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import NotificationPreferences from "@/components/NotificationPreferences";
import { useAuth } from "@/auth";
import { useEntitlement } from "@/entitlement/EntitlementGateContext";
import { ApiError } from "@/lib/api";
import { listIdentities, type UserIdentity } from "@/lib/auth-api";
import { COUNTRIES } from "@/lib/countries";
import {
  cancelSubscription,
  reactivateSubscription,
  type Entitlement,
  type EntitlementTier,
} from "@/lib/entitlement-api";
import { formatLanguageLabel, LANGUAGES, langCodeToI18nLocale, type LangCode } from "@/lib/languages";
import { getProgress } from "@/lib/practice-api";
import {
  getProfile,
  putProfile,
  type UserProfile,
  type UsStateCode as ProfileUsStateCode,
} from "@/lib/profile-api";
import { getState, US_STATES, type UsStateCode } from "@/lib/states";

/**
 * Daily-goal segmented-control values. Stored in localStorage under
 * `rb_daily_goal` per the spec; never round-tripped to the server (it's
 * a UI-only nudge, not a billable rule). Defaults to 25 — middle option,
 * matches the design "★" highlight.
 */
const DAILY_GOAL_VALUES = [10, 25, 50] as const;
type DailyGoalValue = (typeof DAILY_GOAL_VALUES)[number];
const DAILY_GOAL_DEFAULT: DailyGoalValue = 25;
const DAILY_GOAL_STORAGE_KEY = "rb_daily_goal";

function readDailyGoal(): DailyGoalValue {
  if (typeof window === "undefined") return DAILY_GOAL_DEFAULT;
  try {
    const raw = window.localStorage.getItem(DAILY_GOAL_STORAGE_KEY);
    const n = raw ? Number.parseInt(raw, 10) : Number.NaN;
    if (DAILY_GOAL_VALUES.includes(n as DailyGoalValue)) {
      return n as DailyGoalValue;
    }
  } catch {
    // localStorage may be unavailable — fall through to default.
  }
  return DAILY_GOAL_DEFAULT;
}

function writeDailyGoal(value: DailyGoalValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DAILY_GOAL_STORAGE_KEY, String(value));
  } catch {
    // Privacy / quota — UI still flips for the session.
  }
}

/**
 * ISO 3166-1 alpha-2 → flag emoji. Constructed from regional-indicator
 * codepoints (the ASCII letter offset trick — A=0x1F1E6, etc.). Pure
 * function; renders correctly on any system whose font set includes the
 * regional-indicator block (every major platform does).
 */
function flagFor(countryCode: string): string {
  if (!/^[A-Z]{2}$/.test(countryCode)) return "";
  const A = 0x1f1e6;
  const a = "A".charCodeAt(0);
  const codePoints = [
    A + (countryCode.charCodeAt(0) - a),
    A + (countryCode.charCodeAt(1) - a),
  ];
  return String.fromCodePoint(...codePoints);
}

function formatMemberSince(epochMs: number, locale: string): string {
  try {
    return new Date(epochMs).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
    });
  } catch {
    return new Date(epochMs).toISOString().slice(0, 7);
  }
}

/**
 * Map a WorkOS provider wire string (`GoogleOAuth`, `AppleOAuth`,
 * `MicrosoftOAuth`, ...) to a user-facing label. Defensive: any unknown
 * provider falls back to a sanitised version of the wire string (drop
 * the trailing `OAuth` suffix, capitalise) so a new provider added by
 * WorkOS doesn't ship as a raw enum value.
 */
function formatProviderLabel(provider: string): string {
  const stripped = provider.replace(/OAuth$/i, "");
  if (stripped.length === 0) return provider;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

function formatExpiry(epochMs: number, locale: string): string {
  try {
    return new Date(epochMs).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return new Date(epochMs).toISOString().slice(0, 10);
  }
}

interface ProfileLoadState {
  kind: "loading" | "ready" | "error";
  profile?: UserProfile;
  errorMessage?: string;
}

interface ProgressSnapshot {
  attempted: number;
}

export default function ProfilePage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en";
  const { state: authState } = useAuth();
  const { activeEntitlements, isExpired, refresh: refreshEntitlement } =
    useEntitlement();
  const user = authState.kind === "authenticated" ? authState.user : null;

  const [profileState, setProfileState] = useState<ProfileLoadState>({
    kind: "loading",
  });
  const [progress, setProgress] = useState<ProgressSnapshot | null>(null);
  const [dailyGoal, setDailyGoalState] = useState<DailyGoalValue>(
    DAILY_GOAL_DEFAULT,
  );
  const [savingPrefs, setSavingPrefs] = useState<boolean>(false);
  const [prefError, setPrefError] = useState<string | null>(null);
  // Per-card cancel/reactivate flow state (mirrors AccountPage).
  const [cardError, setCardError] = useState<{
    entitlementId: string;
    messageKey: string;
  } | null>(null);

  // ROADS-511: connected OAuth identities (Google, Apple, ...). Read-only
  // for now — WorkOS doesn't expose an unlink endpoint yet, so we show
  // the providers + nudge OAuth-only users toward setting up an email
  // password instead. Initialised to `loading`; settles to `ready`
  // (possibly with empty array = email/password user) or `error`.
  const [identitiesState, setIdentitiesState] = useState<
    | { kind: "loading" }
    | { kind: "ready"; identities: UserIdentity[] }
    | { kind: "error" }
  >({ kind: "loading" });

  // Read daily goal once on mount (SSR-safe).
  useEffect(() => {
    setDailyGoalState(readDailyGoal());
  }, []);

  // Fetch connected identities once on mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const identities = await listIdentities();
        if (!cancelled) setIdentitiesState({ kind: "ready", identities });
      } catch {
        if (!cancelled) setIdentitiesState({ kind: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch profile.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await getProfile();
        if (cancelled) return;
        if (profile === null) {
          setProfileState({
            kind: "error",
            errorMessage: t("profile.errors.noProfile", {
              defaultValue: "No profile found.",
            }),
          });
          return;
        }
        setProfileState({ kind: "ready", profile });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        setProfileState({ kind: "error", errorMessage: message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Fetch progress for the current focus state (best-effort).
  useEffect(() => {
    if (profileState.kind !== "ready") return;
    const focus = profileState.profile?.current_focus_state;
    if (!focus) return;
    let cancelled = false;
    void (async () => {
      try {
        const p = await getProgress(focus);
        if (!cancelled) {
          setProgress({ attempted: p.total_attempted });
        }
      } catch {
        if (!cancelled) setProgress({ attempted: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileState]);

  // Debounced save for target-states + interface-language changes.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushPendingProfile = useCallback(
    async (nextProfile: UserProfile) => {
      setSavingPrefs(true);
      setPrefError(null);
      try {
        const updated = await putProfile({
          home_country: nextProfile.home_country,
          native_language: nextProfile.native_language,
          target_states: nextProfile.target_states,
          current_focus_state: nextProfile.current_focus_state,
          cdl_interest: nextProfile.cdl_interest,
          persona: nextProfile.persona,
        });
        setProfileState({ kind: "ready", profile: updated });
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        setPrefError(message);
      } finally {
        setSavingPrefs(false);
      }
    },
    [],
  );
  const queueSave = useCallback(
    (nextProfile: UserProfile) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        void flushPendingProfile(nextProfile);
      }, 500);
    },
    [flushPendingProfile],
  );

  const onChangeNativeLanguage = useCallback(
    (next: LangCode) => {
      setProfileState((prev) => {
        if (prev.kind !== "ready" || !prev.profile) return prev;
        const nextProfile: UserProfile = {
          ...prev.profile,
          native_language: next,
        };
        queueSave(nextProfile);
        return { kind: "ready", profile: nextProfile };
      });
      // ROADS-529 Phase B follow-up: actually switch the UI locale.
      // Pre-fix, the dropdown saved the user's preference but never called
      // i18n.changeLanguage, so the UI stayed in whatever language was
      // active. Mirrors OnboardingWizard.handleNativeLanguageNext.
      const target = langCodeToI18nLocale(next);
      if (target !== null && target !== i18n.language) {
        void i18n.changeLanguage(target);
      }
    },
    [queueSave, i18n],
  );

  // ROADS-509: home_country / current_focus_state / cdl_interest were
  // previously editable only via the onboarding wizard. Wire them into the
  // same debounced-save path so users never need to "Edit Profile" by
  // running the wizard again.
  const onChangeHomeCountry = useCallback(
    (next: string) => {
      setProfileState((prev) => {
        if (prev.kind !== "ready" || !prev.profile) return prev;
        if (prev.profile.home_country === next) return prev;
        const nextProfile: UserProfile = {
          ...prev.profile,
          home_country: next,
        };
        queueSave(nextProfile);
        return { kind: "ready", profile: nextProfile };
      });
    },
    [queueSave],
  );

  const onChangeFocusState = useCallback(
    (next: UsStateCode) => {
      setProfileState((prev) => {
        if (prev.kind !== "ready" || !prev.profile) return prev;
        if (prev.profile.current_focus_state === next) return prev;
        // Server requires focus_state ∈ target_states; UI already constrains
        // the picker to that set, so just trust the value.
        const nextProfile: UserProfile = {
          ...prev.profile,
          current_focus_state: next as ProfileUsStateCode,
        };
        queueSave(nextProfile);
        return { kind: "ready", profile: nextProfile };
      });
    },
    [queueSave],
  );

  const onToggleCdlInterest = useCallback(
    (next: boolean) => {
      setProfileState((prev) => {
        if (prev.kind !== "ready" || !prev.profile) return prev;
        if (prev.profile.cdl_interest === next) return prev;
        const nextProfile: UserProfile = {
          ...prev.profile,
          cdl_interest: next,
        };
        queueSave(nextProfile);
        return { kind: "ready", profile: nextProfile };
      });
    },
    [queueSave],
  );

  const onRemoveTargetState = useCallback(
    (state: UsStateCode) => {
      setProfileState((prev) => {
        if (prev.kind !== "ready" || !prev.profile) return prev;
        const nextTargets = prev.profile.target_states.filter(
          (s) => s !== state,
        );
        if (nextTargets.length === 0) {
          // Server requires at least one target state — surface inline.
          setPrefError(
            t("profile.errors.atLeastOneState", {
              defaultValue: "Keep at least one target state.",
            }),
          );
          return prev;
        }
        const focus =
          prev.profile.current_focus_state === state
            ? nextTargets[0]
            : prev.profile.current_focus_state;
        const nextProfile: UserProfile = {
          ...prev.profile,
          target_states: nextTargets as ProfileUsStateCode[],
          current_focus_state: focus as ProfileUsStateCode,
        };
        queueSave(nextProfile);
        return { kind: "ready", profile: nextProfile };
      });
    },
    [queueSave, t],
  );

  const onAddTargetState = useCallback(
    (state: UsStateCode) => {
      setProfileState((prev) => {
        if (prev.kind !== "ready" || !prev.profile) return prev;
        if (prev.profile.target_states.includes(state)) return prev;
        const nextTargets = [
          ...prev.profile.target_states,
          state,
        ] as ProfileUsStateCode[];
        const nextProfile: UserProfile = {
          ...prev.profile,
          target_states: nextTargets,
        };
        queueSave(nextProfile);
        return { kind: "ready", profile: nextProfile };
      });
    },
    [queueSave],
  );

  const onSelectDailyGoal = useCallback((next: DailyGoalValue) => {
    setDailyGoalState(next);
    writeDailyGoal(next);
  }, []);

  // Subscription cancel / reactivate — mirrors AccountPage.
  const onCancelSubscription = useCallback(
    async (entitlementId: string) => {
      setCardError(null);
      try {
        await cancelSubscription(entitlementId);
        await refreshEntitlement();
      } catch (err) {
        if (err instanceof ApiError) {
          let key = "profile.membership.error.generic";
          if (err.status === 409) key = "profile.membership.error.notRecurring";
          else if (err.status === 404 || err.status === 403)
            key = "profile.membership.error.notFound";
          else if (err.status === 503)
            key = "profile.membership.error.unavailable";
          setCardError({ entitlementId, messageKey: key });
        } else {
          setCardError({
            entitlementId,
            messageKey: "profile.membership.error.generic",
          });
        }
      }
    },
    [refreshEntitlement],
  );

  const onReactivateSubscription = useCallback(
    async (entitlementId: string) => {
      setCardError(null);
      try {
        await reactivateSubscription(entitlementId);
        await refreshEntitlement();
      } catch (err) {
        const messageKey =
          err instanceof ApiError && err.status === 503
            ? "profile.membership.error.unavailable"
            : "profile.membership.error.generic";
        setCardError({ entitlementId, messageKey });
      }
    },
    [refreshEntitlement],
  );

  const profile =
    profileState.kind === "ready" ? profileState.profile : undefined;

  const homeCountry = useMemo(() => {
    if (!profile) return null;
    return COUNTRIES.find((c) => c.code === profile.home_country) ?? null;
  }, [profile]);

  const focusState = useMemo(() => {
    if (!profile?.current_focus_state) return null;
    return getState(profile.current_focus_state) ?? null;
  }, [profile]);

  const memberSince = user
    ? formatMemberSince(user.created_at_ms, locale)
    : "";

  // Display name: prefer `first_name lastInitial.` (per design), fall
  // back to first_name, then email local-part.
  const displayName = useMemo(() => {
    if (!user) return t("learner.portal.userFallback");
    const first = user.first_name?.trim() ?? "";
    const last = user.last_name?.trim() ?? "";
    if (first && last) return `${first} ${last[0]}.`;
    if (first) return first;
    return user.email.split("@")[0];
  }, [user, t]);

  const initial = (
    user?.first_name?.[0] ?? user?.email?.[0] ?? "L"
  ).toUpperCase();

  // States not yet selected — drive the "+ Add State" picker.
  const selectableStates = useMemo(() => {
    if (!profile) return [];
    return US_STATES.filter(
      (s) => s.active && !profile.target_states.includes(s.code),
    );
  }, [profile]);

  return (
    <LearnerPortalShell pageTitle={t("profile.pageTitle")}>
      <section className="mx-auto w-full max-w-3xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl">
        {profileState.kind === "loading" && (
          <p
            role="status"
            data-testid="profile-loading"
            className="mt-md text-sm text-neutral-700"
          >
            {t("profile.loading")}
          </p>
        )}

        {profileState.kind === "error" && (
          <div
            role="alert"
            data-testid="profile-load-error"
            className="mt-md rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
          >
            {t("profile.loadError", {
              message: profileState.errorMessage ?? "unknown",
            })}
          </div>
        )}

        {profile && (
          <>
            {/* ─── PROFILE HEADER CARD ──────────────────────────────── */}
            <article
              data-testid="profile-header-card"
              className="rounded-lg border border-brand-primary/10 bg-white p-lg shadow-sm sm:p-xl"
            >
              <div className="flex flex-wrap items-start gap-md">
                {/* Avatar */}
                {user?.profile_picture_url ? (
                  <img
                    src={user.profile_picture_url}
                    alt=""
                    className="h-16 w-16 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-teal-100 text-2xl font-bold text-teal-700"
                  >
                    {initial}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h1 className="font-display text-2xl font-bold text-brand-primary">
                    {displayName}
                  </h1>
                  <p className="mt-xxs text-sm text-neutral-700">
                    {user?.email}
                    {memberSince && (
                      <>
                        {" · "}
                        {t("profile.memberSince", { date: memberSince })}
                      </>
                    )}
                  </p>
                  <div className="mt-sm flex flex-wrap gap-sm">
                    {homeCountry && (
                      <span
                        data-testid="profile-home-country-chip"
                        className="inline-flex items-center gap-xxs rounded-full border border-brand-primary/20 bg-brand-surface/40 px-sm py-xxs text-xs font-medium text-neutral-800"
                      >
                        <span aria-hidden="true">
                          {flagFor(homeCountry.code)}
                        </span>
                        <span>
                          {t("profile.chip.home", { country: homeCountry.name })}
                        </span>
                      </span>
                    )}
                    {focusState && (
                      <span
                        data-testid="profile-target-state-chip"
                        className="inline-flex items-center gap-xxs rounded-full border border-brand-primary/20 bg-brand-surface/40 px-sm py-xxs text-xs font-medium text-neutral-800"
                      >
                        <span aria-hidden="true">🎯</span>
                        <span>
                          {t("profile.chip.target", { state: focusState.name })}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                {/*
                  ROADS-509: This CTA used to point at /onboarding?edit=1, which
                  re-ran the wizard from the top — the bug the user-test caught.
                  ProfilePage IS the editor; the CTA now scrolls to the
                  Learning Preferences section where every onboarding-derived
                  field can be changed in place.
                */}
                <a
                  href="#profile-learning-section"
                  data-testid="profile-edit-cta"
                  className="inline-flex shrink-0 items-center justify-center rounded-md border border-brand-primary/30 bg-white px-md py-sm text-sm font-semibold text-brand-primary no-underline hover:bg-brand-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                >
                  {t("profile.editCta")}
                </a>
              </div>

              {/* Three stat pills */}
              <ul
                data-testid="profile-stat-pills"
                className="mt-lg grid grid-cols-1 gap-sm sm:grid-cols-3"
              >
                <StatPill
                  label={t("profile.stats.practice.label")}
                  value={t("profile.stats.practice.value", {
                    count: progress?.attempted ?? 0,
                  })}
                  icon="📋"
                />
                <StatPill
                  label={t("profile.stats.examReady.label")}
                  // Wave 0 placeholder — ROADS-XXX wires this from the latest
                  // exam attempt. Until then we render a benign zero so the
                  // pill still renders without a flash-of-unavailable-data.
                  value={t("profile.stats.examReady.value", { percent: 0 })}
                  icon="🎯"
                />
                <StatPill
                  label={t("profile.stats.streak.label")}
                  value={t("profile.stats.streak.value", { days: 0 })}
                  icon="🔥"
                />
              </ul>
            </article>

            {/* ─── MEMBERSHIP PLAN ──────────────────────────────────── */}
            <section
              aria-labelledby="profile-membership-heading"
              className="mt-lg"
            >
              <h2
                id="profile-membership-heading"
                className="font-display text-lg font-semibold text-brand-primary"
              >
                {t("profile.membership.heading")}
              </h2>
              <div className="mt-sm space-y-md">
                {activeEntitlements.length === 0 ? (
                  <ExpiredOrTrialCard isExpired={isExpired} />
                ) : (
                  activeEntitlements.map((ent) => (
                    <MembershipCard
                      key={ent.id}
                      entitlement={ent}
                      locale={locale}
                      cardError={
                        cardError && cardError.entitlementId === ent.id
                          ? cardError.messageKey
                          : null
                      }
                      onCancel={() => void onCancelSubscription(ent.id)}
                      onReactivate={() =>
                        void onReactivateSubscription(ent.id)
                      }
                    />
                  ))
                )}
              </div>
              <p className="mt-sm text-xs text-neutral-700">
                {t("profile.membership.priceNote")}
              </p>
            </section>

            {/* ─── LEARNING PREFERENCES ─────────────────────────────── */}
            <section
              id="profile-learning-section"
              aria-labelledby="profile-learning-heading"
              className="mt-xl rounded-lg border border-brand-primary/10 bg-white p-lg shadow-sm sm:p-xl"
            >
              <h2
                id="profile-learning-heading"
                className="text-xs font-semibold uppercase tracking-wide text-teal-700"
              >
                {t("profile.learning.heading")}
              </h2>
              <div className="mt-md space-y-md">
                {/* Home Country — ROADS-509: editable in place */}
                <div className="flex flex-wrap items-center justify-between gap-sm">
                  <label
                    htmlFor="profile-home-country-select"
                    className="text-sm font-medium text-neutral-800"
                  >
                    {t("onboarding.review.homeCountryLabel")}
                  </label>
                  <select
                    id="profile-home-country-select"
                    data-testid="profile-home-country-select"
                    value={profile.home_country}
                    onChange={(e) => onChangeHomeCountry(e.target.value)}
                    className="rounded-md border border-brand-primary/20 bg-white px-sm py-xs text-sm text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target States */}
                <div className="flex flex-wrap items-start justify-between gap-sm">
                  <span className="text-sm font-medium text-neutral-800">
                    {t("profile.learning.targetStates")}
                  </span>
                  <div
                    data-testid="profile-target-states"
                    className="flex flex-wrap gap-xxs"
                  >
                    {profile.target_states.map((s) => {
                      const stateMeta = getState(s);
                      return (
                        <span
                          key={s}
                          className="inline-flex items-center gap-xxs rounded-full border border-brand-primary/20 bg-brand-surface/40 px-sm py-xxs text-xs font-medium text-neutral-800"
                        >
                          <span>{stateMeta?.name ?? s}</span>
                          <button
                            type="button"
                            onClick={() => onRemoveTargetState(s)}
                            aria-label={t("profile.learning.removeStateAria", {
                              state: stateMeta?.name ?? s,
                            })}
                            className="ml-xxs inline-flex h-5 w-5 items-center justify-center rounded-full text-neutral-600 hover:bg-white hover:text-danger focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    {selectableStates.length > 0 && (
                      <select
                        data-testid="profile-add-state-picker"
                        value=""
                        onChange={(e) => {
                          const next = e.target.value as UsStateCode;
                          if (next) onAddTargetState(next);
                        }}
                        aria-label={t("profile.learning.addStateAria")}
                        className="rounded-full border border-dashed border-brand-primary/30 bg-white px-sm py-xxs text-xs font-medium text-brand-primary hover:bg-brand-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                      >
                        <option value="">
                          {t("profile.learning.addStateOption")}
                        </option>
                        {selectableStates.map((s) => (
                          <option key={s.code} value={s.code}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Interface Language */}
                <div className="flex flex-wrap items-center justify-between gap-sm">
                  <label
                    htmlFor="profile-interface-language"
                    className="text-sm font-medium text-neutral-800"
                  >
                    {t("profile.learning.interfaceLanguage")}
                  </label>
                  <select
                    id="profile-interface-language"
                    data-testid="profile-language-select"
                    value={profile.native_language}
                    onChange={(e) =>
                      onChangeNativeLanguage(e.target.value as LangCode)
                    }
                    className="rounded-md border border-brand-primary/20 bg-white px-sm py-xs text-sm text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code} lang={l.code}>
                        {formatLanguageLabel(l)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Focus State — ROADS-509: editable in place. Restricted to
                    the current target_states set so the server's invariant
                    (focus_state ∈ target_states) is preserved client-side. */}
                {profile.target_states.length > 1 && (
                  <div className="flex flex-wrap items-center justify-between gap-sm">
                    <label
                      htmlFor="profile-focus-state-select"
                      className="text-sm font-medium text-neutral-800"
                    >
                      {t("onboarding.review.currentFocusStateLabel")}
                    </label>
                    <select
                      id="profile-focus-state-select"
                      data-testid="profile-focus-state-select"
                      value={profile.current_focus_state ?? ""}
                      onChange={(e) =>
                        onChangeFocusState(e.target.value as UsStateCode)
                      }
                      className="rounded-md border border-brand-primary/20 bg-white px-sm py-xs text-sm text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                    >
                      {profile.target_states.map((s) => {
                        const meta = getState(s);
                        return (
                          <option key={s} value={s}>
                            {meta?.name ?? s}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* CDL Interest — ROADS-509: editable in place. */}
                <div className="flex flex-wrap items-center justify-between gap-sm">
                  <label
                    htmlFor="profile-cdl-interest-toggle"
                    className="text-sm font-medium text-neutral-800"
                  >
                    {t("onboarding.review.cdlInterestLabel")}
                  </label>
                  <label className="inline-flex items-center gap-xs">
                    <input
                      id="profile-cdl-interest-toggle"
                      data-testid="profile-cdl-interest-toggle"
                      type="checkbox"
                      checked={profile.cdl_interest}
                      onChange={(e) => onToggleCdlInterest(e.target.checked)}
                      className="h-4 w-4 rounded border-brand-primary/30 text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                    />
                    <span className="text-sm text-neutral-800">
                      {profile.cdl_interest
                        ? t("onboarding.review.cdlYes")
                        : t("onboarding.review.cdlNo")}
                    </span>
                  </label>
                </div>

                {/* Daily Goal */}
                <div className="flex flex-wrap items-center justify-between gap-sm">
                  <span className="text-sm font-medium text-neutral-800">
                    {t("profile.learning.dailyGoal")}
                  </span>
                  <div
                    data-testid="profile-daily-goal"
                    role="group"
                    aria-label={t("profile.learning.dailyGoal")}
                    className="inline-flex rounded-md border border-brand-primary/20 bg-white p-xxs"
                  >
                    {DAILY_GOAL_VALUES.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => onSelectDailyGoal(v)}
                        aria-pressed={dailyGoal === v}
                        className={`min-h-[44px] rounded px-sm py-xxs text-xs font-semibold transition-colors ${
                          dailyGoal === v
                            ? "bg-brand-primary text-white"
                            : "text-brand-primary hover:bg-brand-surface"
                        }`}
                      >
                        {t("profile.learning.dailyGoalOption", { count: v })}
                      </button>
                    ))}
                  </div>
                </div>

                {prefError && (
                  <p
                    role="alert"
                    data-testid="profile-pref-error"
                    className="text-xs text-danger"
                  >
                    {prefError}
                  </p>
                )}
                {savingPrefs && (
                  <p
                    role="status"
                    aria-live="polite"
                    data-testid="profile-pref-saving"
                    className="text-xs text-neutral-600"
                  >
                    {t("profile.learning.saving")}
                  </p>
                )}
              </div>
            </section>

            {/* ─── NOTIFICATIONS ────────────────────────────────────── */}
            <section
              aria-labelledby="profile-notifications-heading"
              className="mt-xl"
            >
              <h2
                id="profile-notifications-heading"
                className="text-xs font-semibold uppercase tracking-wide text-teal-700"
              >
                {t("profile.notifications.heading")}
              </h2>
              <div className="mt-md">
                <NotificationPreferences />
              </div>
            </section>

            {/* ─── ACCOUNT ──────────────────────────────────────────── */}
            <section
              aria-labelledby="profile-account-heading"
              className="mt-xl rounded-lg border border-brand-primary/10 bg-white p-lg shadow-sm sm:p-xl"
            >
              <h2
                id="profile-account-heading"
                className="text-xs font-semibold uppercase tracking-wide text-teal-700"
              >
                {t("profile.account.heading")}
              </h2>
              <div className="mt-md space-y-md">
                {/* ROADS-511: Connected accounts (OAuth identities).
                    Read-only — WorkOS doesn't expose an unlink endpoint
                    yet, so we surface the providers + nudge OAuth-only
                    users toward setting up an email password instead. */}
                <ConnectedAccounts state={identitiesState} />

                <div className="flex flex-wrap items-center justify-between gap-sm">
                  <span className="text-sm font-medium text-neutral-800">
                    {t("profile.account.changePassword.label")}
                  </span>
                  <Link
                    to="/login?reset=1"
                    data-testid="profile-change-password"
                    className="inline-flex items-center justify-center rounded-md border border-brand-primary/30 bg-white px-md py-sm text-sm font-semibold text-brand-primary no-underline hover:bg-brand-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                  >
                    {t("profile.account.changePassword.cta")}
                  </Link>
                </div>
              </div>
            </section>

            {/* ─── DELETE ACCOUNT (Danger Zone) ─────────────────────── */}
            <DangerZone />
          </>
        )}
      </section>
    </LearnerPortalShell>
  );
}

/**
 * ROADS-511 — render the user's connected OAuth identities.
 *
 * UX shape:
 *   - Loading: a small status line.
 *   - Error:   an inline alert. Doesn't block the rest of the page.
 *   - Empty:   "You sign in with your email and password." (the user
 *              has no OAuth identities — they signed up directly.)
 *   - Some:    one chip per provider + a "Set up a password" CTA that
 *              links to the existing /forgot-password flow. This is
 *              the recommended workaround for users who want to be
 *              able to sign in via email if they ever lose access to
 *              their social account. WorkOS doesn't yet expose an
 *              unlink API, so we don't render an Unlink button here —
 *              tracked as a future-work follow-up.
 */
interface ConnectedAccountsProps {
  state:
    | { kind: "loading" }
    | { kind: "ready"; identities: UserIdentity[] }
    | { kind: "error" };
}

function ConnectedAccounts({ state }: ConnectedAccountsProps): JSX.Element {
  const { t } = useTranslation();

  if (state.kind === "loading") {
    return (
      <p
        role="status"
        aria-live="polite"
        data-testid="profile-connected-loading"
        className="text-xs text-neutral-600"
      >
        {t("profile.account.connected.loading")}
      </p>
    );
  }

  if (state.kind === "error") {
    return (
      <p
        role="alert"
        data-testid="profile-connected-error"
        className="text-xs text-danger"
      >
        {t("profile.account.connected.error")}
      </p>
    );
  }

  const hasOAuth = state.identities.length > 0;
  return (
    <div data-testid="profile-connected-accounts" className="space-y-sm">
      <div className="flex flex-wrap items-center justify-between gap-sm">
        <span className="text-sm font-medium text-neutral-800">
          {t("profile.account.connected.heading")}
        </span>
        {hasOAuth ? (
          <ul className="flex flex-wrap gap-xs" role="list">
            {state.identities.map((identity) => (
              <li
                key={identity.provider}
                data-testid={`profile-connected-provider-${identity.provider}`}
                className="inline-flex items-center gap-xxs rounded-full border border-brand-primary/20 bg-brand-surface/40 px-sm py-xxs text-xs font-medium text-neutral-800"
              >
                {formatProviderLabel(identity.provider)}
              </li>
            ))}
          </ul>
        ) : (
          <span
            data-testid="profile-connected-empty"
            className="text-xs text-neutral-700"
          >
            {t("profile.account.connected.empty")}
          </span>
        )}
      </div>
      {hasOAuth && (
        <div className="rounded-md border border-brand-primary/10 bg-brand-surface/30 p-sm">
          <p className="text-xs text-neutral-700">
            {t("profile.account.connected.setupHint")}
          </p>
          <Link
            to="/forgot-password"
            data-testid="profile-connected-setup-cta"
            className="mt-xs inline-flex items-center justify-center rounded-md border border-brand-primary/30 bg-white px-sm py-xs text-xs font-semibold text-brand-primary no-underline hover:bg-brand-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("profile.account.connected.setupCta")}
          </Link>
        </div>
      )}
    </div>
  );
}

interface StatPillProps {
  label: string;
  value: string;
  icon: string;
}

function StatPill({ label, value, icon }: StatPillProps): JSX.Element {
  return (
    <li className="flex items-center gap-sm rounded-md border border-brand-primary/10 bg-brand-surface/40 p-md">
      <span
        aria-hidden="true"
        className="text-xl"
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs text-neutral-600">{label}</p>
        <p className="text-sm font-semibold text-brand-primary">{value}</p>
      </div>
    </li>
  );
}

interface MembershipCardProps {
  entitlement: Entitlement;
  locale: string;
  cardError: string | null;
  onCancel: () => void;
  onReactivate: () => void;
}

function MembershipCard({
  entitlement,
  locale,
  cardError,
  onCancel,
  onReactivate,
}: MembershipCardProps): JSX.Element {
  const { t } = useTranslation();
  const expiry = formatExpiry(entitlement.expires_at_ms, locale);
  const isRecurring = !!entitlement.stripe_subscription_id;
  return (
    <article
      data-testid={`profile-membership-card-${entitlement.id}`}
      className="rounded-md border border-brand-primary/10 bg-white p-md sm:p-lg"
    >
      <div className="flex flex-wrap items-start gap-sm">
        <span className="inline-flex items-center rounded-full bg-brand-primary/10 px-sm py-xxs text-xs font-semibold uppercase tracking-wide text-brand-primary">
          {tierLabel(t, entitlement.tier)}
        </span>
        {entitlement.state_code && (
          <span className="inline-flex items-center rounded-full border border-brand-primary/20 px-sm py-xxs text-xs font-medium text-neutral-800">
            {entitlement.state_code}
          </span>
        )}
      </div>
      <p className="mt-sm text-sm font-semibold text-brand-primary">
        {entitlement.cancel_at_period_end
          ? t("profile.membership.card.cancelScheduled", { date: expiry })
          : t("profile.membership.card.activeUntil", { date: expiry })}
      </p>
      {isRecurring && (
        <div className="mt-sm flex flex-wrap gap-sm">
          {entitlement.cancel_at_period_end ? (
            <button
              type="button"
              onClick={onReactivate}
              data-testid={`profile-membership-reactivate-${entitlement.id}`}
              className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("profile.membership.card.reactivate")}
            </button>
          ) : (
            <button
              type="button"
              onClick={onCancel}
              data-testid={`profile-membership-cancel-${entitlement.id}`}
              className="inline-flex items-center justify-center rounded-md border border-brand-primary/30 bg-white px-md py-sm text-sm font-semibold text-brand-primary hover:bg-brand-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("profile.membership.card.cancel")}
            </button>
          )}
        </div>
      )}
      {!isRecurring && (
        <p className="mt-xs text-xs text-neutral-600">
          {t("profile.membership.card.oneTime")}
        </p>
      )}
      {cardError && (
        <p
          role="alert"
          data-testid={`profile-membership-error-${entitlement.id}`}
          className="mt-sm text-xs text-danger"
        >
          {t(cardError)}
        </p>
      )}
    </article>
  );
}

function ExpiredOrTrialCard({
  isExpired,
}: {
  isExpired: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <article
      data-testid="profile-membership-expired-card"
      className="rounded-md border border-brand-primary/10 bg-white p-md sm:p-lg"
    >
      <p className="text-sm font-semibold text-brand-primary">
        {isExpired
          ? t("profile.membership.expired.heading")
          : t("profile.membership.trial.heading")}
      </p>
      <a
        href="/pricing"
        data-testid="profile-membership-unlock-cta"
        className="mt-sm inline-flex min-h-[44px] items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {t("profile.membership.unlock.cta")}
      </a>
    </article>
  );
}

function tierLabel(
  t: ReturnType<typeof useTranslation>["t"],
  tier: EntitlementTier,
): string {
  return t(`account.card.tierLabel.${tier}` as const);
}
